"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  DEFAULT_MERGE_OPTIONS,
  MAX_COMBINATIONS,
  MERGE_MODES,
  type MergeMode,
  type MergeResult,
  mergeLists,
  parseList,
  UNEVEN_MODES,
  type UnevenMode,
} from "@/lib/tool-kit/text/list-merger";

const MIN_LISTS = 2;
const MAX_LISTS = 6;

const SEPARATORS = {
  space: " ",
  commaSpace: ", ",
  colonSpace: ": ",
  dash: " - ",
  tab: "\t",
  comma: ",",
  none: "",
} as const;

type SeparatorId = keyof typeof SEPARATORS | "custom";
const SEPARATOR_IDS = [...Object.keys(SEPARATORS), "custom"] as SeparatorId[];

/** English strings. A translated content file overrides these through `ui`. */
export const LIST_MERGER_STRINGS = {
  modeLabel: "How to merge",
  modes: {
    zip: "Side by side",
    concatenate: "One after another",
    interleave: "Alternate",
    combinations: "Every combination",
  } as Record<MergeMode, string>,
  modeHints: {
    zip: "Joins line 1 of every list, then line 2, and so on — first names and last names become full names.",
    concatenate: "Puts all of list 1, then all of list 2, into one list.",
    interleave:
      "Takes the first item of each list, then the second of each, and so on.",
    combinations:
      "Pairs every item of each list with every item of the others. The number of lines is the list lengths multiplied together.",
  } as Record<MergeMode, string>,
  listLabel: "List {number}",
  listPlaceholder: "One item per line",
  itemCount: "one: {count} item|other: {count} items",
  addList: "Add a list",
  removeList: "Remove list {number}",
  separatorLabel: "Separator between items",
  separators: {
    space: "Space",
    commaSpace: "Comma and space",
    colonSpace: "Colon and space",
    dash: "Spaced hyphen",
    tab: "Tab",
    comma: "Comma (CSV)",
    none: "Nothing",
    custom: "Custom…",
  } as Record<SeparatorId, string>,
  customSeparatorLabel: "Custom separator",
  unevenLabel: "When the lists are different lengths",
  uneven: {
    pad: "Keep every line and fill the gaps",
    skip: "Keep every line and leave the gaps out",
    shortest: "Stop at the end of the shortest list",
  } as Record<UnevenMode, string>,
  fillLabel: "Fill a missing item with",
  fillPlaceholder: "Nothing",
  trim: "Trim spaces at the ends of items",
  skipEmpty: "Skip blank lines",
  dedupe: "Remove duplicate lines from the result",
  trySample: "Try a sample",
  sampleLists: [
    "Ada\nAlan\nGrace\nKatherine",
    "Lovelace\nTuring\nHopper\nJohnson",
  ],
  clear: "Clear all",
  results: "Merged list",
  emptyPrompt: "Enter items in at least one list, one per line.",
  lineCount: "one: {count} line|other: {count} lines",
  unevenNotice: "The lists have different lengths: {counts}.",
  listCount: "{list}: {items}",
  truncatedNotice:
    "Every combination would make {total} lines, so only the first {max} are shown. Shorten a list to see them all.",
  duplicatesNotice:
    "one: {count} duplicate line removed.|other: {count} duplicate lines removed.",
  copy: "Copy",
  copied: "Copied!",
  download: "Download .txt",
  filename: "merged-list.txt",
} as const;

type Strings = typeof LIST_MERGER_STRINGS;

interface ListBox {
  id: number;
  text: string;
}

function initialLists(): ListBox[] {
  return [
    { id: 1, text: "" },
    { id: 2, text: "" },
  ];
}

function Notices({
  result,
  mode,
  strings,
}: {
  result: MergeResult;
  mode: MergeMode;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const filled = result.counts.filter((count) => count > 0);
  const uneven =
    (mode === "zip" || mode === "interleave") && new Set(filled).size > 1;
  return (
    <>
      {uneven ? (
        <Notice>
          {t(strings.unevenNotice, {
            counts: result.counts
              .map((count, index) =>
                t(strings.listCount, {
                  list: t(strings.listLabel, { number: fmt(index + 1) }),
                  items: tPlural(strings.itemCount, locale, count, {
                    count: fmt(count),
                  }),
                })
              )
              .join(", "),
          })}
        </Notice>
      ) : null}
      {result.truncated ? (
        <Notice>
          {t(strings.truncatedNotice, {
            total: fmt(result.total),
            max: fmt(MAX_COMBINATIONS),
          })}
        </Notice>
      ) : null}
      {result.duplicatesRemoved > 0 ? (
        <p className={HINT}>
          {tPlural(strings.duplicatesNotice, locale, result.duplicatesRemoved, {
            count: fmt(result.duplicatesRemoved),
          })}
        </p>
      ) : null}
    </>
  );
}

export default function ListMergerWidget() {
  const strings = useToolUi(LIST_MERGER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [lists, setLists] = useState<ListBox[]>(initialLists);
  const [nextId, setNextId] = useState(3);
  const [mode, setMode] = useState<MergeMode>(DEFAULT_MERGE_OPTIONS.mode);
  const [separatorId, setSeparatorId] = useState<SeparatorId>("space");
  const [customSeparator, setCustomSeparator] = useState(" | ");
  const [uneven, setUneven] = useState<UnevenMode>(
    DEFAULT_MERGE_OPTIONS.uneven
  );
  const [fill, setFill] = useState("");
  const [trim, setTrim] = useState(true);
  const [skipEmpty, setSkipEmpty] = useState(true);
  const [dedupe, setDedupe] = useState(false);
  const deferredLists = useDeferredValue(lists);
  const separator =
    separatorId === "custom" ? customSeparator : SEPARATORS[separatorId];
  const result = useMemo(
    () =>
      mergeLists(
        deferredLists.map((list) => list.text),
        {
          mode,
          separator,
          uneven,
          fill,
          trim,
          skipEmpty,
          dedupe,
        }
      ),
    [deferredLists, mode, separator, uneven, fill, trim, skipEmpty, dedupe]
  );
  const output = result.lines.join("\n");
  const usesSeparator = mode === "zip" || mode === "combinations";
  const usesUneven = mode === "zip" || mode === "interleave";

  function setText(id: number, text: string) {
    setLists((current) =>
      current.map((list) => (list.id === id ? { ...list, text } : list))
    );
  }

  function addList() {
    setLists((current) => [...current, { id: nextId, text: "" }]);
    setNextId((id) => id + 1);
  }

  function loadSample() {
    setLists(
      strings.sampleLists.map((text, index) => ({ id: index + 1, text }))
    );
    setNextId(strings.sampleLists.length + 1);
    setMode("zip");
    setSeparatorId("space");
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="list-merger-mode"
        onChange={setMode}
        options={MERGE_MODES.map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <p className={HINT}>{strings.modeHints[mode]}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {lists.map((list, index) => {
          const id = `list-merger-${list.id}`;
          const count = parseList(list.text, { trim, skipEmpty }).length;
          return (
            <div className="space-y-2" key={list.id}>
              <div className="flex items-center justify-between gap-2">
                <label className={LABEL} htmlFor={id}>
                  {t(strings.listLabel, { number: fmt(index + 1) })}
                </label>
                {lists.length > MIN_LISTS ? (
                  <button
                    className={BUTTON_SMALL}
                    onClick={() =>
                      setLists((current) =>
                        current.filter((item) => item.id !== list.id)
                      )
                    }
                    type="button"
                  >
                    {t(strings.removeList, { number: fmt(index + 1) })}
                  </button>
                ) : null}
              </div>
              <textarea
                className={`${TEXTAREA} min-h-[160px]`}
                id={id}
                onChange={(event) => setText(list.id, event.target.value)}
                placeholder={strings.listPlaceholder}
                value={list.text}
              />
              <p className={HINT}>
                {tPlural(strings.itemCount, locale, count, {
                  count: fmt(count),
                })}
              </p>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          disabled={lists.length >= MAX_LISTS}
          onClick={addList}
          type="button"
        >
          {strings.addList}
        </button>
        <button className={BUTTON_SECONDARY} onClick={loadSample} type="button">
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setLists(initialLists());
            setNextId(3);
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {usesSeparator ? (
          <SelectField
            id="list-merger-separator"
            label={strings.separatorLabel}
            onChange={setSeparatorId}
            options={SEPARATOR_IDS.map((value) => ({
              value,
              label: strings.separators[value],
            }))}
            value={separatorId}
          />
        ) : null}
        {usesSeparator && separatorId === "custom" ? (
          <TextField
            id="list-merger-custom"
            label={strings.customSeparatorLabel}
            mono
            onChange={setCustomSeparator}
            value={customSeparator}
          />
        ) : null}
        {usesUneven ? (
          <SelectField
            id="list-merger-uneven"
            label={strings.unevenLabel}
            onChange={setUneven}
            options={UNEVEN_MODES.map((value) => ({
              value,
              label: strings.uneven[value],
            }))}
            value={uneven}
          />
        ) : null}
        {usesUneven && uneven === "pad" ? (
          <TextField
            id="list-merger-fill"
            label={strings.fillLabel}
            onChange={setFill}
            placeholder={strings.fillPlaceholder}
            value={fill}
          />
        ) : null}
      </div>
      <div className="space-y-2">
        <Checkbox checked={trim} label={strings.trim} onChange={setTrim} />
        <Checkbox
          checked={skipEmpty}
          label={strings.skipEmpty}
          onChange={setSkipEmpty}
        />
        <Checkbox
          checked={dedupe}
          label={strings.dedupe}
          onChange={setDedupe}
        />
      </div>
      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result.lines.length === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <Notices mode={mode} result={result} strings={strings} />
            <label className={LABEL} htmlFor="list-merger-output">
              {tPlural(strings.lineCount, locale, result.lines.length, {
                count: fmt(result.lines.length),
              })}
            </label>
            <textarea
              className={TEXTAREA}
              id="list-merger-output"
              readOnly
              value={output}
            />
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={output}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() => downloadText(strings.filename, `${output}\n`)}
                type="button"
              >
                {strings.download}
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
